import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, CircleAlert, Info, X } from 'lucide-react';
import { cn } from '@/lib/cn';

export type ToastTone = 'success' | 'error' | 'info' | 'warning' | 'danger' | 'neutral';

export interface ToastOptions {
  title: string;
  description?: ReactNode;
  tone?: ToastTone;
  /** Milliseconds; 0 keeps the toast until dismissed. Errors default to 8s, others 4.5s. */
  duration?: number;
  action?: { label: string; onClick: () => void };
}

interface ToastItem extends ToastOptions {
  id: number;
  tone: ToastTone;
}

export interface ToastApi {
  (options: ToastOptions): number;
  toast: (options: ToastOptions) => number;
  show: (options: ToastOptions) => number;
  success: (title: string, description?: ReactNode) => number;
  error: (title: string, description?: ReactNode) => number;
  info: (title: string, description?: ReactNode) => number;
  warning: (title: string, description?: ReactNode) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);
const MAX_VISIBLE = 4;

const toneStyles: Record<ToastTone, { icon: typeof Info; accent: string; iconClass: string }> = {
  success: { icon: CheckCircle2, accent: 'bg-success-500', iconClass: 'text-success-600' },
  error: { icon: CircleAlert, accent: 'bg-danger-500', iconClass: 'text-danger-600' },
  danger: { icon: CircleAlert, accent: 'bg-danger-500', iconClass: 'text-danger-600' },
  info: { icon: Info, accent: 'bg-sapphire-500', iconClass: 'text-sapphire-600 dark:text-sapphire-300' },
  warning: { icon: AlertTriangle, accent: 'bg-warning-500', iconClass: 'text-warning-600' },
  neutral: { icon: Info, accent: 'bg-navy-400', iconClass: 'text-ink-muted' },
};

function ToastCard({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: number) => void }) {
  const [paused, setPaused] = useState(false);
  const duration = toast.duration ?? (toast.tone === 'error' ? 8000 : 4500);
  useEffect(() => {
    if (paused || duration === 0) {
      return;
    }
    const id = window.setTimeout(() => onDismiss(toast.id), duration);
    return () => window.clearTimeout(id);
  }, [paused, duration, toast.id, onDismiss]);
  const t = toneStyles[toast.tone];
  const Icon = t.icon;
  return (
    <div
      role={toast.tone === 'error' ? 'alert' : 'status'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className="glass-strong pointer-events-auto relative flex w-full animate-slide-up gap-3 overflow-hidden rounded-xl py-3 pl-5 pr-3 shadow-pop ring-1 ring-line"
    >
      <span className={cn('absolute inset-y-0 left-0 w-1', t.accent)} aria-hidden="true" />
      <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', t.iconClass)} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink">{toast.title}</p>
        {toast.description && <p className="mt-0.5 text-sm text-ink-muted">{toast.description}</p>}
        {toast.action && (
          <button
            type="button"
            onClick={() => {
              toast.action?.onClick();
              onDismiss(toast.id);
            }}
            className="mt-1.5 text-sm font-semibold text-sapphire-700 hover:underline dark:text-sapphire-300"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button type="button" aria-label="Dismiss notification" onClick={() => onDismiss(toast.id)} className="-mr-1 -mt-1 h-7 w-7 shrink-0 rounded-md text-ink-subtle transition hover:bg-sunken hover:text-ink">
        <X className="mx-auto h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => setToasts((prev) => prev.filter((t) => t.id !== id)), []);
  const show = useCallback((options: ToastOptions) => {
    const id = nextId.current++;
    const tone = options.tone === 'danger' ? 'error' : (options.tone ?? 'info');
    setToasts((prev) => [...prev, { ...options, id, tone }].slice(-MAX_VISIBLE));
    return id;
  }, []);

  const api = useMemo<ToastApi>(() => {
    const fn = ((options: ToastOptions) => show(options)) as ToastApi;
    fn.toast = (options: ToastOptions) => show(options);
    fn.show = show;
    fn.dismiss = dismiss;
    fn.success = (title, description) => show({ title, description, tone: 'success' });
    fn.error = (title, description) => show({ title, description, tone: 'error' });
    fn.info = (title, description) => show({ title, description, tone: 'info' });
    fn.warning = (title, description) => show({ title, description, tone: 'warning' });
    return fn;
  }, [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[80] flex flex-col items-end gap-2 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:bottom-4 sm:right-4 sm:left-auto sm:w-[26rem] sm:p-0">
        {toasts.map((toast) => (
          <ToastCard key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/** `const toast = useToast(); toast.success('Patron saved')`. */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return ctx;
}
